<template>
  <v-container fluid>
    <PageHeaderSlot showBackButton>
      <template slot="bottom-right-slot">
        <FormButton text="create" class="ml-2" @click="save()" />
      </template>
    </PageHeaderSlot>

    <v-form ref="form" @submit.prevent>
      <v-card>
        <v-card-text class="px-2">
          <v-row class="ma-0">
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormSelect
                label="optionCourse"
                placeholder="optionCourse"
                :fieldValue.sync="formData.course_id"
                :options="courseOptions"
                required
                @changed="onCourseChanged()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3" v-if="gDisableEmptyCode">
              <FormInput label="courseCode" placeholder="courseCode" :fieldValue.sync="formData.course_code" required />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormInput
                label="courseName"
                placeholder="courseName"
                :fieldValue.sync="formData.course_name"
                required
                @input-value="generateTimetablePreview()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormInput
                type="number"
                label="price"
                placeholder="price"
                :fieldValue.sync="formData.price"
                prefix="HK$"
                suffix="perLesson"
                required
                isStrictFloat
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormSelect
                v-if="isAppMode(['T01', 'T16'])"
                label="tutor"
                placeholder="selectTutor"
                :fieldValue.sync="formData.tutor_id"
                :options="tutorOptions"
                :required="!isAppMode(['T02'])"
                :enableClear="isAppMode(['T02'])"
                @changed="onTutorChanged(false)"
              />
              <FormSelect
                v-else
                label="tutor"
                placeholder="selectTutor"
                :fieldValue.sync="formData.tutor_id_list"
                :options="tutorOptions"
                :required="!isAppMode(['T02'])"
                isMultiple
                @changed="onTutorChanged()"
              />
            </v-col>
            <template v-if="isAppMode(['T16'])">
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormSelect
                  label="salaryMethodType"
                  placeholder="selectSalaryMethod"
                  :fieldValue.sync="formData.t16_salary_type"
                  :options="$salaryTypeOptions"
                  required
                  @changed="onSalaryTypeChanged()"
                />
              </v-col>
              <template v-if="formData.t16_salary_type === 'percentage'">
                <v-col cols="12" sm="6" md="4" lg="3">
                  <FormSelect
                    label="salaryMethod"
                    placeholder="selectSalaryMethod"
                    :fieldValue.sync="formData.salary_method"
                    :options="$salaryMethodOptions"
                    required
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <FormInput
                    type="number"
                    label="salary"
                    placeholder="salary"
                    :fieldValue.sync="formData.t16_salary"
                    prefix="HK$"
                    required
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4" lg="3">
                  <FormInput
                    type="number"
                    label="salaryMethodOptions.percentage"
                    placeholder="salaryMethodOptions.percentage"
                    :fieldValue.sync="formData.t16_salary_percentage"
                    suffix="%"
                    required
                  />
                </v-col>
              </template>
              <v-col cols="12" sm="6" md="4" lg="3" v-else-if="formData.t16_salary_type === 'hour'">
                <FormInput
                  type="number"
                  label="hourlyWage"
                  :fieldValue.sync="formData.t16_salary_per_hour"
                  prefix="HK$"
                  suffix="perHour"
                  required
                />
              </v-col>
            </template>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormSelect
                label="classroom"
                placeholder="selectClassroom"
                :fieldValue.sync="formData.classroom_id"
                :options="classroomOptions"
                :required="!isAppMode(['T16','T79'])"
                :enableClear="isAppMode(['T16','T79'])"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <CourseColorPicker
                label="timetableColor"
                placeholder="selectTimetableColor"
                :fieldValue.sync="formData.color"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3" v-if="isAppMode(['T01'])">
              <FormSelect
                label="salaryMethod"
                placeholder="selectSalaryMethod"
                :fieldValue.sync="formData.salary_method"
                :options="$salaryMethodOptions"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <FormSelect
                label="studentUpperLimit"
                placeholder="selectStudentUpperLimit"
                :fieldValue.sync="formData.max_student"
                :options="maxStudentOptions"
                required
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3" v-if="isAppMode(['T04'])">
              <FormSelect
                label="lessonSchoolType"
                placeholder="lessonSchoolType"
                :fieldValue.sync="formData.school_type"
                :options="schoolTypeOptions"
                required
              />
            </v-col>
            <template v-if="!gDisableCourseToOnline">
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormSelect
                  label="showLessonInOnline"
                  placeholder="showLessonInOnline"
                  :fieldValue.sync="formData.show_website"
                  :options="displayOnlineOptions"
                  required
                />
              </v-col>
              <template v-if="formData.show_website">
                <v-col cols="12" sm="6" md="4" lg="3">
                  <div style="height: 25px;" class="d-none d-sm-block"></div>
                  <FormUploader
                    uploadText="uploadThumbnail"
                    fileInputKey="upload_thumbnail"
                    :files.sync="formData.thumbnail_image_list"
                    dispatchUpdateOnChange
                    single
                  />
                </v-col>
                <v-col cols="12">
                  <QuillEditor
                    :html.sync="formData.course_description"
                    editorID="description"
                    label="courseDescription"
                  />
                </v-col>
              </template>
            </template>
          </v-row>

          <v-row class="ma-0">
            <v-col cols="12" sm="6" md="4" lg="3">
              <label class="input-label">{{ $t('lessonGenSetting') }}</label>
              <v-radio-group
                name="lessonType"
                v-model="lessonType"
                row
                :readonly="$store.getters.isLoading"
                @change="refreshGenerateSettings()"
              >
                <v-radio :label="$t('regularLesson')" value="regular"></v-radio>
                <v-radio :label="$t('singleLesson')" value="single"></v-radio>
              </v-radio-group>
            </v-col>
            <template v-if="lessonType === 'regular'">
              <v-col cols="12" sm="6" md="4" lg="3">
                <label class="input-label">{{ $t('regularLessonPeriod') }}</label>
                <v-radio-group
                  name="regular_period"
                  v-model="formData.regular_period"
                  row
                  :readonly="$store.getters.isLoading"
                  @change="generateTimetablePreview()"
                >
                  <v-radio :label="$t('weekly')" value="weekly"></v-radio>
                  <v-radio :label="$t('biweekly')" value="bi-weekly"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormDatePicker
                  label="courseStartDate"
                  :dateValue.sync="formData.regular_start_date"
                  required
                  @changed="
                    formData.regular_end_date = ''
                    generateTimetablePreview()
                  "
                  disabledActionButtons
                  :holidayData="holidayList"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormDatePicker
                  label="courseEndDate"
                  :dateValue.sync="formData.regular_end_date"
                  required
                  @changed="generateTimetablePreview()"
                  :maxDate="regularEndDateMax"
                  :minDate="formData.regular_start_date"
                  :disabled="!$validate.DataValid(formData.regular_start_date)"
                  disabledActionButtons
                  :holidayData="holidayList"
                />
              </v-col>
              <v-col cols="12">
                <label class="input-label">{{ $t('lessonPeriod') }}</label>
                <v-row class="ma-0">
                  <v-checkbox
                    v-for="item in weekdayOptions"
                    :key="item.value"
                    v-model="formData.regular_weekdays"
                    :label="item.text"
                    color="primary"
                    :value="item.value"
                    hide-details
                    class="mr-12"
                    @click="generateTimetablePreview()"
                  ></v-checkbox>
                </v-row>
                <div
                  class="v-text-field__details mt-2"
                  v-if="isFormSubmitClicked && !$validate.DataValid(formData.regular_weekdays)"
                >
                  <div class="v-messages theme--light error--text" role="alert">
                    <div class="v-messages__wrapper">
                      <div class="v-messages__message">{{ $t('selectLessonPeriod') }}</div>
                    </div>
                  </div>
                </div>
              </v-col>
            </template>
            <v-col cols="12" sm="6" md="4" lg="3" v-else-if="lessonType === 'single'">
              <FormDatePicker
                label="singleLessonDate"
                :dateValue.sync="formData.single_date"
                required
                disabledActionButtons
                @changed="generateTimetablePreview()"
                :customRules="[v => checkLessonDateValid(formData.single_date)]"
                :holidayData="holidayList"
              />
            </v-col>
          </v-row>
          <v-row class="ma-0">
            <v-col cols="12" sm="8" md="6" lg="4" v-if="lessonType === 'regular'">
              <label class="input-label">{{ $t('time') }}</label>
              <template v-if="sortedSelectedWeekdays.length > 0">
                <div class="d-flex justify-space-between" v-for="day in sortedSelectedWeekdays">
                  <span class="body-2 colorBlack--text text-no-wrap mt-3 mr-5">{{ weekdayOptions[day].text }} : </span>
                  <FormRangeTimePicker
                    :startTimeValue.sync="formData.start_times[day]"
                    :endTimeValue.sync="formData.end_times[day]"
                    required
                    @changed="generateTimetablePreview()"
                  />
                </div>
              </template>
              <span class="body-2" v-else>{{ $t('noLessonPeriod') }}</span>
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="3" v-else-if="lessonType === 'single'">
              <FormRangeTimePicker
                label="time"
                :startTimeValue.sync="formData.start_time"
                :endTimeValue.sync="formData.end_time"
                required
                @changed="generateTimetablePreview()"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>

    <Datatable
      v-if="!isAppMode(['T01', 'T16'])"
      class="mb-4 mt-4"
      tableName="tutor"
      :tableData="tutorTableData"
      :tableHeaders="tutorTableHeaders"
      disablePagination
      @edit-clicked="handleTutorSalary($event)"
    >
      <template v-slot:[`item.salary_method`]="{ item }">
        <span v-if="item.salary_method === 'percentage'">
          {{ $t(`salaryMethodOptions.${item.salary_method}`) }}: {{ item.percentage }}%
        </span>
        <span v-else-if="item.salary_method === 'hour'">
          {{ $t(`salaryMethodOptions.${item.salary_method}`) }}: HK${{ item.salary_per_hour }}
        </span>
        <span v-else-if="item.salary_method === 'level'">
          {{ $t(`salaryMethodOptions.${item.salary_method}`) }}: {{ gfSalaryLevelName(item.salary_level) }}
          {{
            item.salary_level && $validate.DataValid(item.salary_level_price) ? ` (HK$${item.salary_level_price})` : ''
          }}
        </span>
      </template>
    </Datatable>

    <Datatable
      class="mt-4"
      tableName="lessonPreview"
      tableSmallText="canEditAfterCreateLesson"
      tableEmptyText="noSchedule"
      :tableData="scheduleTableData"
      :tableHeaders="scheduleTableHeaders"
      disablePagination
    />

    <!-- Modal: 導師薪酬計算 -->
    <FormDialog title="editTutorSalary" ref="tutorFormDialog" formRef="tutorForm" :maxWidth="600">
      <v-row class="ma-0">
        <v-col class="py-0" cols="12" sm="6">
          <FormSelect
            label="salaryMethodType"
            placeholder="selectSalaryMethod"
            :fieldValue.sync="tutorSalaryData.salary_method"
            :options="$salaryTypeOptions"
            required
            @changed="onSalaryMethodChanged()"
          />
        </v-col>
        <v-col class="py-0" cols="12" sm="6" v-if="tutorSalaryData.salary_method === 'level'">
          <FormSelect
            label="salaryLevel"
            placeholder="selectSalaryLevel"
            :fieldValue.sync="tutorSalaryData.salary_level"
            :options="salaryLevelOptions"
            required
          />
        </v-col>
        <template v-else-if="tutorSalaryData.salary_method === 'percentage'">
          <v-col class="py-0" cols="12" sm="6">
            <FormInput
              type="number"
              label="sharePercentage"
              :fieldValue.sync="tutorSalaryData.percentage"
              suffix="%"
              required
            />
          </v-col>
          <v-col class="py-0" cols="12" sm="6">
            <FormInput type="number" label="minSalary" :fieldValue.sync="tutorSalaryData.min_salary" prefix="HK$" />
          </v-col>
        </template>
        <template v-else-if="tutorSalaryData.salary_method === 'hour'">
          <v-col class="py-0" cols="12" sm="6">
            <FormInput
              type="number"
              label="hourlyWage"
              :fieldValue.sync="tutorSalaryData.salary_per_hour"
              prefix="HK$"
              suffix="perHour"
            />
          </v-col>
        </template>
        <template v-if="isAppMode(['T21'])">
          <v-col class="py-0" cols="12" sm="6">
            <FormInput type="number" label="bonusRnD" :fieldValue.sync="tutorSalaryData.bonus_rnd" prefix="HK$" />
          </v-col>
          <v-col class="py-0" cols="12" sm="6">
            <FormInput
              type="number"
              label="bonusPerformance"
              :fieldValue.sync="tutorSalaryData.bonus_performance"
              prefix="HK$"
            />
          </v-col>
          <v-col class="py-0" cols="12" sm="6">
            <FormInput
              type="number"
              label="bonusAttendance"
              :fieldValue.sync="tutorSalaryData.bonus_attendance"
              prefix="HK$"
            />
          </v-col>
        </template>
      </v-row>
    </FormDialog>
  </v-container>
</template>

<script>
import FormInput from '@/components/formField/FormInput.vue'
import FormSelect from '@/components/formField/FormSelect.vue'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import CourseColorPicker from '@/components/CourseColorPicker.vue'
import Datatable from '@/components/Datatable.vue'
import QuillEditor from '@/components/formField/QuillEditor.vue'
import FormDialog from '@/components/FormDialog.vue'
import FormUploader from '@/components/formField/FormUploader.vue'
import FormRangeTimePicker from '@/components/formField/FormRangeTimePicker.vue'

export default {
  name: 'CreateClass',
  components: {
    FormInput,
    FormSelect,
    FormDatePicker,
    CourseColorPicker,
    Datatable,
    QuillEditor,
    FormDialog,
    FormUploader,
    FormRangeTimePicker,
  },
  computed: {
    regularEndDateMax() {
      if (this.$validate.DataValid(this.formData.regular_start_date)) {
        const maxDate = this.$formatter.convertDate(this.formData.regular_start_date)
        maxDate.setFullYear(maxDate.getFullYear() + 1)
        return this.$formatter.formatDate(maxDate)
      }

      return ''
    },
    sortedSelectedWeekdays() {
      return this.formData.regular_weekdays.sort((a, b) => a - b)
    },
    tutorTableHeaders() {
      const vm = this
      let headers = [
        { value: 'name', text: vm.$t('tutor'), width: 200 },
        { value: 'salary_method', text: vm.$t('salaryMethodType') },
        { value: 'min_salary', text: `${vm.$t('minSalary')} (HK$)` },
      ]

      if (this.isAppMode(['T21'])) {
        headers = [
          ...headers,
          { value: 'bonus_rnd', text: `${vm.$t('bonusRnD')} (HK$)` },
          { value: 'bonus_performance', text: `${vm.$t('bonusPerformance')} (HK$)` },
          { value: 'bonus_attendance', text: `${vm.$t('bonusAttendance')} (HK$)` },
          { value: 'total_bonus', text: `${vm.$t('totalBonus')} (HK$)` },
        ]
      }

      headers.push({ value: 'minActionEdit', text: '', align: 'end' })

      return headers
    },
    scheduleTableHeaders() {
      const vm = this
      let headers = [
        { value: 'course_name', text: vm.$t('course'), width: 220 },
        { value: 'weekday', text: vm.$t('weekday'), width: 220 },
        { value: 'full_date', text: vm.$t('date'), width: 220 },
        { value: 'timeslot', text: vm.$t('time'), width: 220 },
      ]
      if (!this.disableCourseGrade) {
        headers.push({ value: 'grade', text: vm.$t('grade') })
      }

      return headers
    },
  },
  data: vm => ({
    disableCourseGrade: false,
    isFormSubmitClicked: false,

    lessonType: 'regular', // 'regular' | 'single'
    formData: {
      course_id: '',
      course_code: '',
      course_name: '',
      tutor_id: '',
      tutor_id_list: [],
      classroom_id: null,
      color: '',
      salary_method: 'by_lesson',
      salary_level: null,
      max_student: 1,
      price: vm.isAppMode(['T38']) ? '0' : '',
      start_time: '',
      end_time: '',
      start_times: ['', '', '', '', '', '', ''],
      end_times: ['', '', '', '', '', '', ''],
      // for regular
      regular_period: 'weekly', //  'weekly' | 'bi-weekly'
      regular_weekdays: [],
      regular_start_date: '',
      regular_end_date: '',
      // for single date
      single_date: '',

      course_description: '',
      thumbnail_image_list: [],
      show_website: false,

      // only for T04
      school_type: '',

      // only for T16
      t16_salary: '',
      t16_salary_percentage: '',
      t16_salary_per_hour: '',
      t16_salary_type: null,
    },
    courseOptions: [],
    tutorOptions: [],
    classroomOptions: [],
    scheduleTableData: [],

    tutorTableData: [],
    tutorSalaryData: {
      salary_method: 'level',
      salary_level: '',
      min_salary: '',
      percentage: '',
      salary_per_hour: '',
      // -- for T21 only
      bonus_rnd: '',
      bonus_performance: '',
      bonus_attendance: '',
    },

    // ---- static
    weekdayOptions: [
      { value: 0, text: vm.$t('weekdays[0]') },
      { value: 1, text: vm.$t('weekdays[1]') },
      { value: 2, text: vm.$t('weekdays[2]') },
      { value: 3, text: vm.$t('weekdays[3]') },
      { value: 4, text: vm.$t('weekdays[4]') },
      { value: 5, text: vm.$t('weekdays[5]') },
      { value: 6, text: vm.$t('weekdays[6]') },
    ],
    maxStudentOptions: [],
    displayOnlineOptions: [
      { value: true, text: vm.$t('display') },
      { value: false, text: vm.$t('notDisplay') },
    ],
    holidayList: [],
    schoolTypeOptions: [
      { value: 'A', text: vm.$t('lessonSchoolTypeOptions.A') },
      { value: 'B', text: vm.$t('lessonSchoolTypeOptions.B') },
      { value: 'all', text: vm.$t('lessonSchoolTypeOptions.all') },
    ],
    salaryLevelOptions: [],
  }),
  methods: {
    async generateTimetablePreview() {
      this.scheduleTableData = []

      let gradeText = ''
      const selectedCourse = this.courseOptions.find(el => el.value === this.formData.course_id)
      if (this.$validate.DataValid(selectedCourse)) {
        gradeText = selectedCourse.grade_name
      }

      if (
        this.lessonType === 'regular' &&
        this.$validate.DataValid(this.formData.regular_weekdays) &&
        this.$validate.DataValid(this.formData.regular_start_date) &&
        this.$validate.DataValid(this.formData.regular_end_date)
      ) {
        const startDate = this.$formatter.convertDate(this.formData.regular_start_date)
        const minDate = this._getMinDate(startDate)
        const maxDate = this.$formatter.convertDate(this.formData.regular_end_date)
        const dates = []
        const selectedWeekdays = this._sortWeekdays(minDate.getDay())

        // 跟星期Row攞 (e.g. bi-weekly (thu, tue): ['2023-12-07', '2023-12-19', '2023-12-21'])
        const d = this.$formatter.convertDate(this.formData.regular_start_date)
        const tempDate = this._getMinDate(d)
        let currentPos = 0

        while (tempDate.getTime() <= maxDate.getTime()) {
          if (tempDate.getTime() <= maxDate.getTime()) {
            dates.push(this.$formatter.formatDate(tempDate))
          }

          if (this.formData.regular_period === 'weekly') {
            if (selectedWeekdays.length > 1) {
              const currentDay = selectedWeekdays[currentPos]
              let nextDay = null
              if (currentPos < selectedWeekdays.length - 1) {
                nextDay = selectedWeekdays[currentPos + 1]
              } else {
                nextDay = selectedWeekdays[0]
              }

              if (nextDay < currentDay) {
                tempDate.setDate(tempDate.getDate() + (7 - currentDay) + nextDay)
              } else {
                tempDate.setDate(tempDate.getDate() + (nextDay - currentDay))
              }

              if (currentPos < selectedWeekdays.length - 1) {
                currentPos++
              } else {
                currentPos = 0
              }
            } else {
              tempDate.setDate(tempDate.getDate() + 7)
            }
          } else if (this.formData.regular_period === 'bi-weekly') {
            if (selectedWeekdays.length > 1) {
              const currentDay = selectedWeekdays[currentPos]
              let nextDay = null
              if (currentPos < selectedWeekdays.length - 1) {
                nextDay = selectedWeekdays[currentPos + 1]
              } else {
                nextDay = selectedWeekdays[0]
              }

              if (nextDay > currentDay) {
                tempDate.setDate(tempDate.getDate() + (nextDay - currentDay))
              } else {
                tempDate.setDate(tempDate.getDate() + (7 - currentDay) + nextDay + 7)
              }

              if (currentPos < selectedWeekdays.length - 1) {
                currentPos++
              } else {
                currentPos = 0
              }
            } else {
              tempDate.setDate(tempDate.getDate() + 14)
            }
          }
        }

        dates.sort((a, b) => this.$formatter.sortDate(a, b))

        const holidayDates = this.holidayList.map(el => el.date)
        dates.forEach(el => {
          if (!holidayDates.includes(el)) {
            const weekday = this.$formatter.getDay(el)
            this.scheduleTableData.push({
              course_name: this.formData.course_name,
              weekday: weekday,
              date: el,
              start_time: this.formData.start_times[weekday],
              end_time: this.formData.end_times[weekday],
              grade: gradeText,
            })
          }
        })
      } else if (this.lessonType === 'single' && this.$validate.DataValid(this.formData.single_date)) {
        this.scheduleTableData.push({
          course_name: this.formData.course_name,
          weekday: this.$formatter.getDay(this.formData.single_date),
          date: this.formData.single_date,
          start_time: this.formData.start_time,
          end_time: this.formData.end_time,
          grade: gradeText,
        })
      }
    },
    checkLessonDateValid(date) {
      if (this.$validate.DataValid(date)) {
        const holidayDates = this.holidayList.map(el => el.date)
        if (holidayDates.includes(date)) {
          return this.$t('message.isHolidayLesson')
        }
        return true
      }
      return false
    },
    refreshGenerateSettings() {
      this.isFormSubmitClicked = false
      this.formData.regular_weekdays = []
      this.formData.regular_period = 'weekly'
      this.formData.single_date = ''
      this.formData.start_time = ''
      this.formData.end_time = ''
      this.formData.start_times = ['', '', '', '', '', '', '']
      this.formData.end_times = ['', '', '', '', '', '', '']
      this.generateTimetablePreview()
    },
    _getMinDate(d) {
      const minDate = d
      if (this.lessonType === 'regular' && this.$validate.DataValid(this.formData.regular_weekdays)) {
        if (this.formData.regular_weekdays.includes(minDate.getDay())) {
          return minDate
        } else {
          const currentDate = minDate.getDate()
          minDate.setDate(currentDate + 1)
          return this._getMinDate(d)
        }
      }

      const dateS = this.$formatter.formatDate(minDate)
      return this.$formatter.convertDate(dateS)
    },
    _sortWeekdays(minWeekday) {
      if (this.$validate.DataValid(this.formData.regular_weekdays)) {
        const weekdays = this.formData.regular_weekdays
        let closestDay = null
        for (let i = 0; i < weekdays.length; i++) {
          if (weekdays[i] === minWeekday) {
            closestDay = weekdays[i]
            break
          } else if (closestDay !== null) {
            let diff_min_closest = 0
            if (closestDay > minWeekday) {
              diff_min_closest = closestDay - minWeekday
            } else {
              diff_min_closest = 7 - minWeekday + closestDay
            }

            let diff_min_current = 0
            if (weekdays[i] > minWeekday) {
              diff_min_current = weekdays[i] - minWeekday
            } else {
              diff_min_current = 7 - minWeekday + weekdays[i]
            }

            if (diff_min_closest > diff_min_current) {
              closestDay = weekdays[i]
            }

            continue
          } else {
            closestDay = weekdays[i]
            continue
          }
        }

        let list = []
        let temp = closestDay
        while (temp < 7) {
          if (this.formData.regular_weekdays.includes(temp)) {
            list.push(temp)
          }
          temp++
        }

        let minTemp = 0
        while (minTemp < closestDay) {
          if (this.formData.regular_weekdays.includes(minTemp)) {
            list.push(minTemp)
          }
          minTemp++
        }

        return list
      }

      return []
    },

    onCourseChanged() {
      const found = this.courseOptions.find(el => el.value === this.formData.course_id)
      if (found) {
        if (this.isAppMode(['T22', 'T37', 'T45', 'T49','T61','T79', 'T81'])) {
          this.formData.course_name = found.text
        }

        if (!this.isAppMode(['T01'])) {
          this.formData.color = found.color || ''
        }
      }

      this.generateTimetablePreview()
    },
    onSalaryTypeChanged() {
      if (this.formData.t16_salary_type === 'percentage') {
        this.formData.salary_method = 'by_student_num'
      } else {
        this.formData.salary_method = 'by_lesson'
      }
    },

    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      // check未有center，reject
      const checkSchool = this.gfCheckSchoolSelected()
      if (!checkSchool) return

      this.isFormSubmitClicked = true
      const valid = this.$refs['form'].validate()
      if (!valid || (this.lessonType === 'regular' && !this.$validate.DataValid(this.formData.regular_weekdays))) {
        this.$store.dispatch('toggleSnack', { message: 'required' })
        return
      }

      this.$store.dispatch('setLoading', true)
      const selectedCourse = this.courseOptions.find(el => el.value === this.formData.course_id)
      const selectedSchool = this.gfGetUserSelectedSchool()

      let payload = {
        course_id: parseInt(this.formData.course_id),
        course_code: this.formData.course_code.trim(),
        course_name: this.formData.course_name,
        tutor_id: -1,
        classroom_id: this.$validate.DataValid(this.formData.classroom_id) ? parseInt(this.formData.classroom_id) : -1,
        color: this.formData.color,
        salary_method: this.formData.salary_method || 'by_lesson',
        max_student: parseInt(this.formData.max_student),
        school_id: selectedSchool,
        price: parseFloat(this.formData.price),
        grade: selectedCourse.grade,
      }
      if (this.isAppMode(['T04'])) {
        payload['school_type'] = this.formData.school_type
      } else if (this.isAppMode(['T16'])) {
        payload['t16_salary_type'] = this.formData.t16_salary_type || ''
        if (this.formData.t16_salary_type === 'percentage') {
          payload['t16_salary'] = parseFloat(this.formData.t16_salary)
          payload['t16_salary_percentage'] = parseFloat(this.formData.t16_salary_percentage)
        } else if (this.formData.t16_salary_type === 'hour') {
          payload['t16_salary_per_hour'] = parseFloat(this.formData.t16_salary_per_hour)
        }
      }

      if (!this.gDisableCourseToOnline) {
        payload['thumbnail_image'] = this.formData.thumbnail_image_list.length
          ? this.formData.thumbnail_image_list[0].file
          : ''
        payload['course_description'] = this.formData.course_description
        payload['show_website'] = this.formData.show_website
      }

      if (this.isAppMode(['T01', 'T16'])) {
        // single tutor
        if (this.$validate.DataValid(this.formData.tutor_id)) {
          payload['tutor_id'] = parseInt(this.formData.tutor_id)
        }
      } else {
        // multiple tutor, different salary method options, each tutor one salary method
        payload['salary_level'] = this.formData.salary_level || ''
        payload['salary_method'] = 'by_lesson'

        payload['tutor_id_list'] = this.formData.tutor_id_list

        payload['salary_type'] = []
        payload['salary_per_hour'] = []
        payload['salary_percentage'] = []
        payload['multi_salary_level'] = []
        payload['salary_bonus'] = []
        for (let i = 0; i < this.tutorTableData.length; i++) {
          payload['salary_type'].push({
            staff_id: this.tutorTableData[i].id,
            type: this.tutorTableData[i].salary_method,
          })
          if (this.tutorTableData[i].salary_method === 'hour') {
            payload['salary_per_hour'].push({
              staff: this.tutorTableData[i].id,
              value: this.tutorTableData[i].salary_per_hour,
              min: 0,
            })
          } else if (this.tutorTableData[i].salary_method === 'percentage') {
            payload['salary_percentage'].push({
              staff: this.tutorTableData[i].id,
              value: this.tutorTableData[i].percentage,
              min: this.tutorTableData[i].min_salary,
            })
          } else if (this.tutorTableData[i].salary_method === 'level') {
            payload['multi_salary_level'].push({
              staff_id: this.tutorTableData[i].id,
              level: this.tutorTableData[i].salary_level,
            })
          }

          payload['salary_bonus'].push({
            staff_id: this.tutorTableData[i].id,
            rnd: this.tutorTableData[i].bonus_rnd,
            performance: this.tutorTableData[i].bonus_performance,
            attendance: this.tutorTableData[i].bonus_attendance,
          })
        }

        if (!this.isAppMode(['T21'])) {
          delete payload['salary_bonus']
        }
      }

      if (this.lessonType === 'regular') {
        payload['regular_period'] = this.formData.regular_period
        payload['regular_weekdays'] = this.formData.regular_weekdays.sort((a, b) => a - b)
        payload['start_date'] = this.formData.regular_start_date
        payload['end_date'] = this.formData.regular_end_date
        payload['weekday_start_time'] = []
        payload['weekday_end_time'] = []

        payload['regular_weekdays'].forEach(weekday => {
          payload['weekday_start_time'].push({ weekday: weekday, time: this.formData.start_times[weekday] })
          payload['weekday_end_time'].push({ weekday: weekday, time: this.formData.end_times[weekday] })
        })
      } else if (this.lessonType === 'single') {
        payload['single_date'] = this.formData.single_date
        payload['lesson_type'] = 'single'
        payload['start_time'] = this.formData.start_time
        payload['end_time'] = this.formData.end_time
      }

      try {
        if (this.lessonType === 'regular') {
          await this.$Fetcher.NewRegularLesson(payload)
        } else if (this.lessonType === 'single') {
          await this.$Fetcher.NewSingleDateLesson(payload)
        } else {
          throw 'message.errorOccur'
        }
        // clear cache timetable lessons
        this.$store.dispatch('setTimetableCache');

        this.$store.dispatch('toggleSnack', {
          message: 'message.createSuccess',
          type: 'success',
          redirect: 'Timetable',
        })
      } catch (err) {
        this.$common.error(err)
        this.showAlert({
          title: 'message.createFail',
          message: err,
          isError: true,
        })
      } finally {
        this.$store.dispatch('setLoading', false)
      }
    },

    // ================ 導師薪酬計算 ================
    onTutorChanged(multiple = true) {
      const userType = this.gfGetUserType()
      const canViewSalary = this.$validate.allowAction('viewSalary', userType)

      let tutorIdList = []
      if (multiple) {
        tutorIdList = this.formData.tutor_id_list
      } else if (this.formData.tutor_id !== null && this.formData.tutor_id > -1) {
        tutorIdList = [this.formData.tutor_id]

        if (this.isAppMode(['T16']) && canViewSalary) {
          const found = this.tutorOptions.find(el => el.value === this.formData.tutor_id)
          this.formData.t16_salary_per_hour = found ? found.salary_per_hour || 0 : 0
        }
      }

      this.tutorTableData = this.tutorTableData.filter(item => tutorIdList.includes(item.id))

      for (let i = 0; i < tutorIdList.length; i++) {
        const foundPos = this.tutorTableData.findIndex(el => el.id === tutorIdList[i])
        if (foundPos < 0) {
          const found = this.tutorOptions.find(el => el.value === tutorIdList[i])
          if (found) {
            this.tutorTableData.push({
              id: tutorIdList[i],
              name: found.text,
              salary_level: '',
              salary_method: 'level',
              min_salary: 0,
              percentage: 0,
              salary_per_hour: 0,
              default_salary_per_hour: canViewSalary ? found.salary_per_hour || 0 : 0,
              bonus_rnd: 0,
              bonus_performance: 0,
              bonus_attendance: 0,
              total_bonus: 0,
            })
          }
        }
      }
    },

    async handleTutorSalary(id = null, newOpen = true) {
      if (newOpen) {
        if (this.$store.getters.isLoading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }

        for (let key in this.tutorSalaryData) {
          if (key === 'salary_method') {
            this.$set(this.tutorSalaryData, key, 'level')
          } else {
            this.$set(this.tutorSalaryData, key, '')
          }
        }
        this.salaryLevelOptions = []
        await this.$refs.tutorFormDialog.resetForm()
        if (this.$validate.DataValid(id)) {
          const found = this.tutorTableData.find(el => el.id === id)

          for (let key in this.tutorSalaryData) {
            if (this.$validate.DataValid(found[key])) {
              this.$set(this.tutorSalaryData, key, found[key])
            }
          }

          const foundTutor = this.tutorOptions.find(el => el.id === id)
          this.$salaryLevelOptions.forEach(item => {
            this.salaryLevelOptions.push({
              value: item.value,
              text: `${item.text} (HK$${
                foundTutor?.salary_level && foundTutor.salary_level[item.value]
                  ? foundTutor.salary_level[item.value]
                  : 0
              })`,
            })
          })
          this.$refs.tutorFormDialog.setInfo(id)
        } else {
          return
        }
      } else {
        this.$refs.tutorFormDialog.setLoading(false)
      }

      const confirm = await this.$refs.tutorFormDialog.show()
      if (confirm) {
        const foundSame = this.tutorTableData.findIndex(el => el.id == id)
        if (foundSame > -1) {
          this.tutorTableData[foundSame].salary_level = this.tutorSalaryData.salary_level
          this.tutorTableData[foundSame].salary_level_price = 0
          if (this.tutorSalaryData.salary_level) {
            const foundTutor = this.tutorOptions.find(el => el.id === id)
            if (foundTutor?.salary_level && foundTutor.salary_level[this.tutorSalaryData.salary_level]) {
              this.tutorTableData[foundSame].salary_level_price =
                foundTutor.salary_level[this.tutorSalaryData.salary_level]
            }
          }
          this.tutorTableData[foundSame].salary_method = this.tutorSalaryData.salary_method

          this.tutorTableData[foundSame].min_salary =
            this.tutorSalaryData.salary_method === 'percentage' && this.tutorSalaryData.min_salary
              ? parseFloat(this.tutorSalaryData.min_salary)
              : 0
          this.tutorTableData[foundSame].percentage =
            this.tutorSalaryData.salary_method === 'percentage' && this.tutorSalaryData.percentage
              ? parseFloat(this.tutorSalaryData.percentage)
              : 0
          this.tutorTableData[foundSame].salary_per_hour =
            this.tutorSalaryData.salary_method === 'hour' && this.tutorSalaryData.salary_per_hour
              ? parseFloat(this.tutorSalaryData.salary_per_hour)
              : 0

          const bonus_rnd = this.tutorSalaryData.bonus_rnd ? parseFloat(this.tutorSalaryData.bonus_rnd) : 0
          const bonus_performance = this.tutorSalaryData.bonus_performance
            ? parseFloat(this.tutorSalaryData.bonus_performance)
            : 0
          const bonus_attendance = this.tutorSalaryData.bonus_attendance
            ? parseFloat(this.tutorSalaryData.bonus_attendance)
            : 0
          this.tutorTableData[foundSame].bonus_rnd = bonus_rnd
          this.tutorTableData[foundSame].bonus_performance = bonus_performance
          this.tutorTableData[foundSame].bonus_attendance = bonus_attendance
          this.tutorTableData[foundSame].total_bonus = bonus_rnd + bonus_performance + bonus_attendance
        }
        this.$store.dispatch('setDataIsUpdated', true)
        this.$refs.tutorFormDialog.hide()
      }
    },
    onSalaryMethodChanged() {
      this.tutorSalaryData.salary_level = ''
      this.tutorSalaryData.min_salary = ''
      this.tutorSalaryData.percentage = ''
      this.tutorSalaryData.salary_per_hour = ''
      if (this.tutorSalaryData.salary_method === 'hour') {
        const getId = this.$refs.tutorFormDialog.getInfo()
        const found = this.tutorTableData.find(el => el.id == getId)
        if (found) {
          this.tutorSalaryData.salary_per_hour = found.default_salary_per_hour
        }
      }
    },

    // ----- Get Options -----
    async _getCourseOptions() {
      try {
        let payload = {
          join_category_data: true,
        }
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center_id'] = selectedSchool

        const { data } = await this.$Fetcher.GetCourses(payload)
        this.courseOptions = await this.gfSortCourseOptions(data)
      } catch (err) {
        this.$common.error(err)
      }
    },
    async _getTutorOptions() {
      try {
        let payload = {}
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center'] = [selectedSchool]

        const { data } = await this.$Fetcher.GetStaffs(payload)
        this.tutorOptions = data.map(el => {
          return {
            value: el.id,
            text: el.name,
            ...el,
          }
        })
      } catch {}
    },
    async _getClassroomOptions() {
      try {
        let payload = {}
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) payload['center_id'] = selectedSchool
        const { data } = await this.$Fetcher.GetClassrooms(payload)
        this.classroomOptions = data.map(el => {
          return {
            value: el.id,
            text: el.name,
          }
        })
      } catch {}
    },
    async _getHolidays() {
      if (this.$validate.DataValid(this.$excludeMenuList) && this.$excludeMenuList.includes('setting-holiday')) return

      try {
        const selectedSchool = this.gfGetUserSelectedSchool()
        this.holidayList = await this.$Fetcher.GetHolidaysWithCenter(selectedSchool)
      } catch {}
    },
    async _getSetting() {
      let data = null
      if (this.gEnableCustomGrade) {
        try {
          data = await this.$Fetcher.GetSetting()
        } catch (err) {
          this.$common.error(err)
        }
      }
      this.disableCourseGrade = this.gfCheckDisabledCourseGrade(data)
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: CreateClass')
      const check = this.gfCheckSchoolSelected(true)
      if (check) {
        await this._getSetting()
        await this._getCourseOptions()
        await this._getTutorOptions()
        await this._getClassroomOptions()
        await this._getHolidays()
      }
      this.$store.dispatch('toggleLoadingPage', false)
    },
  },
  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  async created() {
    const list = []
    for (let i = 1; i <= 30; i++) {
      list.push({
        value: i,
        text: this.$t('person', { count: i }),
      })
    }
    if (!this.isAppMode(['T01'])) {
      list.unshift({ value: -1, text: this.$t('noLimit') })
    }
    this.maxStudentOptions = list
  },
  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else {
      next()
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-input--checkbox .v-label {
  color: var(--v-colorBlack-base) !important;
  font-size: 0.875rem !important;
}
</style>
